import { CellContext, Row } from '@tanstack/react-table'
import {
  commonActionColumn,
  renderGradeColumn,
  renderHeaderTooltip
} from '@utils/assessmentReportUtils'
import { Progress } from 'antd'

import {
  Database_Procs_KASPercentAggregatedByLevel_Item,
  Database_Procs_TeamKnowAndShow_Item
} from '@suntex.fim/api-client'
import { $KnowAndShowCategory } from '@suntex.fim/enums'
import { ColumnType, Tooltip } from '@suntex.fim/ui'

import {
  generateScrollableAssessmentCommonColumns,
  generateScrollableAssessmentCommonColumnsForSchool,
  renderWrappedAssessmentHeader
} from './CommonColumns'

export type KASReportData =
  | (Database_Procs_KASPercentAggregatedByLevel_Item &
      Partial<Database_Procs_TeamKnowAndShow_Item>)
  | Database_Procs_TeamKnowAndShow_Item

// order must match KnowAndShowCategory enum values (1-9) since it drives the ks_${level}_${n} accessor index
export const ALL_COLUMN_KEYS = $KnowAndShowCategory
  .describeAll()
  .map((desc) => desc.slug)

const CATEGORY_SELECT_LABELS = $KnowAndShowCategory
  .describeAll()
  .reduce<Record<string, string>>((labels, desc) => {
    labels[desc.slug] = desc.label
    return labels
  }, {})

export const HIDABLE_SELECT_LABELS: Record<string, string> = {
  userId: 'User ID',
  displayName: 'Display Name',
  stickers: 'Stickers',
  ...CATEGORY_SELECT_LABELS
}

export const SCHOOL_HIDABLE_SELECT_LABELS: Record<string, string> = {
  name: 'Name',
  desc: 'Class Name',
  contactName: 'Teacher',
  grade: 'Grade',
  students: 'Students',
  stickers: 'Stickers',
  ...CATEGORY_SELECT_LABELS
}

/**
 * Generates progress bar cell renderer for columns
 */
const createProgressCellRenderer = (
  graphColor: string,
  level: number,
  keyIndex: number
) => {
  function ProgressCellRenderer({
    row: { original }
  }: Readonly<CellContext<KASReportData, unknown>>) {
    const value = original
      ? original[`ks_${level}_${keyIndex + 1}` as keyof KASReportData]
      : 0
    return (
      <Tooltip
        title={`${Number(value) || 0}% complete`}
        classNames={{ root: 'progress-tooltip' }}
        placement="right"
        arrow={false}
      >
        <Progress
          strokeColor={graphColor}
          percent={Number(value) || 0}
          size={['100%', 32]}
          showInfo={false}
        />
      </Tooltip>
    )
  }
  ProgressCellRenderer.displayName = 'ProgressCellRenderer'
  return ProgressCellRenderer
}

/**
 * Generates level-specific columns
 */
const createLevelColumns = (
  columnKeys: readonly string[],
  labels: Record<string, string>,
  graphColor: string,
  level: number
): ColumnType<KASReportData>[] =>
  columnKeys.map((key, i) => ({
    header: () => renderWrappedAssessmentHeader(`${labels[key]}`),
    accessorFn: (resultData: KASReportData) =>
      resultData
        ? resultData[`ks_${level}_${i + 1}` as keyof KASReportData]
        : 0,
    id: key,
    size: 160,
    cell: createProgressCellRenderer(graphColor, level, i)
  }))

/**
 * Returns final columns for the Know and Show report table
 */
export const columns = ({
  level,
  graphColor,
  sendMessage
}: {
  level: number
  graphColor: string
  sendMessage: (t: Row<KASReportData>[]) => void
}) => {
  const columnKeys = level > 2 ? ALL_COLUMN_KEYS : ALL_COLUMN_KEYS.slice(0, 4)

  return [
    commonActionColumn<KASReportData>(HIDABLE_SELECT_LABELS, sendMessage),
    ...generateScrollableAssessmentCommonColumns<KASReportData>(),
    ...createLevelColumns(columnKeys, HIDABLE_SELECT_LABELS, graphColor, level)
  ]
}

/**
 * Returns final columns for the school-level Know and Show report table
 */
export const schoolColumns = ({
  level,
  graphColor
}: {
  level: number
  graphColor: string
}) => {
  const schoolCommonColumns =
    generateScrollableAssessmentCommonColumnsForSchool<KASReportData>()
  const columnKeys = level > 2 ? ALL_COLUMN_KEYS : ALL_COLUMN_KEYS.slice(0, 4)

  return [
    ...schoolCommonColumns.slice(0, 3),
    {
      header: () => renderHeaderTooltip('Grade'),
      size: 110,
      accessorKey: 'grade',
      cell: (info: any) => {
        return renderGradeColumn(info.getValue())
      },
      id: 'grade',
      enableSorting: true
    },
    ...schoolCommonColumns.slice(4),
    ...createLevelColumns(
      columnKeys,
      SCHOOL_HIDABLE_SELECT_LABELS,
      graphColor,
      level
    )
  ]
}
